import { Modal } from 'antd';
import { isBoolean, isEmpty, isEqual, isFinite, merge, omit, pick, pickBy, uniq } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type AttributeFormLayout, type AttributeLayout } from 'components/damages/attributeLayout';
import { OVERRIDABLE_COMPUTED_METRIC_ATTRIBUTE_KEYS } from 'components/damages/constants';
import { useDamageAttrSelections } from 'components/damages/withDamages';
import { useMessage } from 'components/ui/Message';
import { isExternalReferenceTooLong } from 'horizon/components/Damages/ExternalReference';
import { type AtlasGqlAsset, useGetDamageSchemaNamesQuery } from 'types/atlas-graphql';
import { DAMAGE_EDIT_ATTRIBUTES } from 'utils/access-control/rules';
import { useFeatures } from 'utils/features';
import { usePermissions } from 'utils/usePermissions';

import { AttributesForm } from './AttributesForm';
import { type ExternalAttrSelection, resolveExternalAttrSelection } from './externalSelections';
import { type DamageEditPayload } from './types';

interface Props {
  attributeLayout?: AttributeLayout;
  backgroundColor?: string;
  damage: {
    attrs: Record<string, any>;
  };
  formLayout?: AttributeFormLayout;
  ignoreWorkInProgress?: boolean;
  isImageViewer?: boolean;
  loading: boolean;
  onCancel?: () => void;
  onSave?: (payload: DamageEditPayload) => void | Promise<void>;
  onAttrsChange?: (attrs: Record<string, any>) => void;
  onSaveDisabledChange?: (disabled: boolean) => void;
  editAttrs: Record<string, any>;
  potentialAttrs: Record<string, any>;
  potentialAssets: {
    likely: AtlasGqlAsset[];
    nearby: AtlasGqlAsset[];
  };
  onRequestDeltaTMeasurement?: () => void;
  deltaTMeasurementEnabled?: boolean;
  externalAttrSelections?: ExternalAttrSelection[];
  onExternalAttrSelectionsApplied?: (requestIds: number[]) => void;
  isNewObservation?: boolean;
  canChangeAsset?: boolean;
  canChangeSchema?: boolean;
  assetId?: string;
  selectedAsset?: AtlasGqlAsset | null;
  assetSelectionScopeId?: string;
  schemaId?: string;
  critical?: boolean;
  externalReference?: {
    externalId?: string | null;
    externalIdSource?: string | null;
  };
  shapeChanged: boolean;
  shapeRevision?: number;
  shapeDrawn: boolean;
  shouldHideRealtimePotentialAttrs?: boolean;
  showComment?: boolean;
}

export default function EditableDamageAttributes({
  attributeLayout,
  backgroundColor,
  damage,
  formLayout = 'vertical',
  ignoreWorkInProgress = false,
  isImageViewer = false,
  loading,
  onCancel,
  onSave,
  onAttrsChange,
  onSaveDisabledChange,
  editAttrs,
  potentialAttrs,
  potentialAssets,
  onRequestDeltaTMeasurement,
  deltaTMeasurementEnabled = false,
  externalAttrSelections = [],
  onExternalAttrSelectionsApplied,
  isNewObservation = false,
  canChangeAsset = false,
  canChangeSchema = false,
  assetId,
  selectedAsset,
  assetSelectionScopeId,
  schemaId,
  critical,
  externalReference,
  shapeChanged,
  shapeRevision,
  shapeDrawn,
  shouldHideRealtimePotentialAttrs = false,
  showComment,
}: Props) {
  const { t } = useTranslation();
  const message = useMessage();
  const [saving, setSaving] = useState<boolean>(false);
  const [hasChanged, setHasChanged] = useState<boolean>(false);
  const [combinationsSatisfied, setCombinationsSatisfied] = useState<boolean>(false);
  const [workingAssetId, setWorkingAssetId] = useState<string | undefined>();
  const [workingSchemaId, setWorkingSchemaId] = useState<string | undefined>();
  const [workingCritical, setWorkingCritical] = useState<boolean | undefined>();
  const initialExternalId = externalReference?.externalId ?? '';
  const initialExternalIdSource = externalReference?.externalIdSource ?? '';
  const [workingExternalId, setWorkingExternalId] = useState(initialExternalId);
  const [workingExternalIdSource, setWorkingExternalIdSource] = useState(initialExternalIdSource);
  const [warningModalOpen, setWarningModalOpen] = useState<boolean>(false);
  const [payload, setPayload] = useState<DamageEditPayload>({});
  const [localCurrentAttrs, setLocalCurrentAttrs] = useState<Record<string, any>>({});
  const [clearedAttrs, setClearedAttrs] = useState<string[]>([]);
  const [resetComputedAttrs, setResetComputedAttrs] = useState<string[]>([]);
  // Each box edit invalidates overrides entered for the previous shape.
  const currentShapeRevision = shapeRevision ?? Number(Boolean(shapeChanged));
  const [localShapeRevision, setLocalShapeRevision] = useState(currentShapeRevision);
  const shapeRevisionRef = useRef(currentShapeRevision);
  shapeRevisionRef.current = currentShapeRevision;
  const appliedAttrRequestIdsRef = useRef<Set<number>>(new Set());
  const { REQUIRE_VALID_DAMAGE } = useFeatures().features;
  const { canAccess, hasRole } = usePermissions();
  const isEditor = hasRole('role-editor');
  const canPreserveComputedMetrics = canAccess([DAMAGE_EDIT_ATTRIBUTES]);

  useEffect(() => {
    if (localShapeRevision !== currentShapeRevision) {
      setLocalCurrentAttrs(current => omit(current, OVERRIDABLE_COMPUTED_METRIC_ATTRIBUTE_KEYS));
      setResetComputedAttrs([]);
      setLocalShapeRevision(currentShapeRevision);
    }
  }, [currentShapeRevision, localShapeRevision]);

  const preservedRealtimeAttrs = useMemo(
    () =>
      canPreserveComputedMetrics
        ? omit(
            {
              ...(isNewObservation || shapeChanged
                ? {}
                : pick(damage?.attrs ?? {}, OVERRIDABLE_COMPUTED_METRIC_ATTRIBUTE_KEYS)),
              ...(localShapeRevision === currentShapeRevision
                ? pick(localCurrentAttrs, OVERRIDABLE_COMPUTED_METRIC_ATTRIBUTE_KEYS)
                : {}),
            },
            resetComputedAttrs
          )
        : {},
    [
      canPreserveComputedMetrics,
      damage?.attrs,
      isNewObservation,
      localCurrentAttrs,
      localShapeRevision,
      resetComputedAttrs,
      shapeChanged,
      currentShapeRevision,
    ]
  );

  const { data: schemaData } = useGetDamageSchemaNamesQuery({
    variables: { ids: uniq([schemaId, workingSchemaId].filter(Boolean)) },
    skip: !schemaId,
  });

  const {
    columns,
    selections,
    hiddenSelections,
    disabledSelections,
    clearSelection,
    makeSelection,
    clearSelections,
    loading: selectableLoading,
    includesRealtimePotentialAttrs,
  } = useDamageAttrSelections({
    schemaId: workingSchemaId,
    currentAttrs: {
      ...(isNewObservation ? editAttrs : damage?.attrs),
      ...localCurrentAttrs,
    },
    potentialAttrs,
    preservedRealtimeAttrs,
    clearedAttrs,
    ignoreWorkInProgress,
    shouldHideRealtimePotentialAttrs,
    shouldHideHiddenAttrs: true,
    shouldHideDisabledAttrs: false,
  });

  const jsonSelections = useMemo(() => {
    return merge(
      {},
      ...[...selections, ...hiddenSelections, ...disabledSelections].map(
        ({ name, date, string, number }) => ({
          [name]: string || date || number,
        })
      )
    );
  }, [selections, hiddenSelections, disabledSelections]);

  useEffect(() => {
    if (isNewObservation && !isEqual(editAttrs, jsonSelections)) {
      onAttrsChange && onAttrsChange(jsonSelections);
    }
  }, [editAttrs, isNewObservation, onAttrsChange, jsonSelections]);

  useEffect(() => {
    /**
     * If filters are available for an attribute, it must have a value for currently available
     * schema combinations. `combinationsSatisfied` is used to indicate whether the form can be submitted
     * based on whether the selected values are a valid combination in the schema.
     */
    if (REQUIRE_VALID_DAMAGE) {
      setCombinationsSatisfied(
        !columns.some(
          ({ filters, key }) => !!filters.length && !Object.keys(jsonSelections).includes(key)
        )
      );
    }
  }, [columns, jsonSelections, REQUIRE_VALID_DAMAGE]);

  // set assetId
  useEffect(() => {
    if (assetId) {
      setWorkingAssetId(assetId);
    }
  }, [assetId]);

  // set schemaId
  useEffect(() => {
    if (schemaId) {
      setWorkingSchemaId(schemaId);
    }
  }, [schemaId]);

  // set critical status
  useEffect(() => {
    if (isBoolean(critical)) {
      setWorkingCritical(critical);
    }
  }, [critical]);

  useEffect(() => {
    setWorkingExternalId(initialExternalId);
    setWorkingExternalIdSource(initialExternalIdSource);
  }, [initialExternalId, initialExternalIdSource]);

  const externalReferenceChanged =
    !!externalReference &&
    (workingExternalId.trim() !== initialExternalId.trim() ||
      workingExternalIdSource.trim() !== initialExternalIdSource.trim());
  const externalReferenceTooLong =
    !!externalReference &&
    (isExternalReferenceTooLong(workingExternalId) ||
      isExternalReferenceTooLong(workingExternalIdSource));

  // Need this setState to avoid react state update on unmounted component issues
  useEffect(() => {
    return () => {
      setSaving(false);
    };
  }, [warningModalOpen]);

  useEffect(() => {
    if (!damage?.attrs && !Object.keys(jsonSelections).length) {
      setHasChanged(externalReferenceChanged);
      return;
    }

    // Allow saving if:
    // - the shape has changed
    // - the schema has changed
    // - the current critical status is different
    // - the current attributes are different from what has been selected (or)
    // - some of the attributes are no longer allowed (i.e. they will be deleted) (or)
    setHasChanged(
      Boolean(
        shapeChanged ||
          schemaId !== workingSchemaId ||
          assetId !== workingAssetId ||
          critical !== workingCritical ||
          externalReferenceChanged ||
          !isEqual(damage?.attrs, jsonSelections) ||
          disabledSelections.length
      )
    );
  }, [
    disabledSelections,
    jsonSelections,
    damage,
    shapeChanged,
    schemaId,
    workingSchemaId,
    assetId,
    workingAssetId,
    critical,
    workingCritical,
    externalReferenceChanged,
  ]);

  const handleChangeWorkingSchemaId = useCallback(
    (newWorkingSchemaId: string | undefined) => {
      clearSelections();
      setLocalCurrentAttrs({});
      setClearedAttrs([]);
      setResetComputedAttrs([]);
      if (isNewObservation) {
        onAttrsChange && onAttrsChange({});
      }
      setWorkingSchemaId(newWorkingSchemaId);
    },
    [clearSelections, isNewObservation, onAttrsChange]
  );

  const handleOnChange = useCallback(
    selection => {
      if (currentShapeRevision !== shapeRevisionRef.current) {
        return;
      }
      const { name, ..._rest } = selection;
      const rest = pickBy(_rest, a => a !== undefined);
      const nextValue = rest.string ?? rest.number ?? rest.date;
      const isOverridableComputedMetric =
        OVERRIDABLE_COMPUTED_METRIC_ATTRIBUTE_KEYS.includes(name) && isFinite(potentialAttrs[name]);
      const matchesComputedValue =
        isOverridableComputedMetric &&
        nextValue !== null &&
        nextValue !== undefined &&
        Number(nextValue) === potentialAttrs[name];

      if (isOverridableComputedMetric) {
        setResetComputedAttrs(current =>
          matchesComputedValue
            ? uniq([...current, name])
            : current.filter(resetName => resetName !== name)
        );
      }

      const cleared = nextValue === null || nextValue === undefined;
      setLocalCurrentAttrs(current =>
        cleared || (matchesComputedValue && !shouldHideRealtimePotentialAttrs)
          ? omit(current, name)
          : { ...current, [name]: nextValue }
      );
      setClearedAttrs(current =>
        cleared ? uniq([...current, name]) : current.filter(clearedName => clearedName !== name)
      );

      if (cleared) {
        clearSelection({ name });
      } else {
        makeSelection(selection);
      }
    },
    [
      makeSelection,
      clearSelection,
      potentialAttrs,
      shouldHideRealtimePotentialAttrs,
      currentShapeRevision,
    ]
  );

  useEffect(() => {
    if (!externalAttrSelections.length) {
      return;
    }

    const appliedRequestIds: number[] = [];

    externalAttrSelections.forEach(externalAttrSelection => {
      if (appliedAttrRequestIdsRef.current.has(externalAttrSelection.requestId)) {
        return;
      }

      const resolvedSelection = resolveExternalAttrSelection(columns, externalAttrSelection);
      if (!resolvedSelection) {
        return;
      }

      appliedAttrRequestIdsRef.current.add(externalAttrSelection.requestId);
      appliedRequestIds.push(externalAttrSelection.requestId);

      setLocalCurrentAttrs(current => ({
        ...current,
        [externalAttrSelection.name]: resolvedSelection.nextValue,
      }));
      setClearedAttrs(current =>
        current.filter(clearedName => clearedName !== externalAttrSelection.name)
      );

      makeSelection(resolvedSelection.selection);
    });

    if (appliedRequestIds.length) {
      onExternalAttrSelectionsApplied?.(appliedRequestIds);
    }
  }, [columns, externalAttrSelections, makeSelection, onExternalAttrSelectionsApplied]);

  const _handleSave = useCallback(
    async (payload: DamageEditPayload) => {
      if (onSave) {
        setSaving(true);
        try {
          await onSave(payload);
        } catch {
          message.error(t('damage.saveFailed', 'Failed to save damage'));
        } finally {
          setSaving(false);
        }
      }
    },
    [message, onSave, t]
  );

  const handleSave = useCallback(
    (payload: DamageEditPayload) => {
      const { assetId, schemaId: schemaIdToSave, critical, comment } = payload;

      const _payload = {
        assetId,
        schemaId: schemaIdToSave,
        critical,
        attrs: [...selections, ...hiddenSelections],
        comment,
        ...(externalReferenceChanged && {
          externalId: workingExternalId.trim(),
          externalIdSource: workingExternalIdSource.trim(),
        }),
      };

      if (canChangeSchema && schemaId && schemaIdToSave !== schemaId) {
        setPayload(_payload);
        setWarningModalOpen(true);
      } else {
        _handleSave(_payload);
      }
    },
    [
      selections,
      schemaId,
      canChangeSchema,
      _handleSave,
      hiddenSelections,
      externalReferenceChanged,
      workingExternalId,
      workingExternalIdSource,
    ]
  );

  const handleCancel = useCallback(() => onCancel?.(), [onCancel]);

  const sourceSchemaName = (schemaData?.damageSchemas ?? []).find(
    ({ id }) => id === schemaId
  )?.name;
  const targetSchemaName = (schemaData?.damageSchemas ?? []).find(
    ({ id }) => id === workingSchemaId
  )?.name;

  const warningModalCopy =
    sourceSchemaName && targetSchemaName
      ? t(
          'damage.changeSchemaWarningNamed',
          'You have changed the damage schema for this damage from {{sourceSchemaName}} to {{targetSchemaName}}. Any damage attributes from {{sourceSchemaName}} that are not in {{targetSchemaName}} will be removed upon saving. Do you want to continue?',
          { sourceSchemaName, targetSchemaName }
        )
      : t(
          'damage.changeSchemaWarning',
          'You have changed the damage schema for this damage. Any damage attributes from the current schema that are not in the new schema will be removed upon saving. Do you want to continue?'
        );

  const isSaveDisabled = Boolean(
    !shapeDrawn ||
      !onSave ||
      !hasChanged ||
      (REQUIRE_VALID_DAMAGE && !combinationsSatisfied) ||
      (canChangeSchema && !workingSchemaId) ||
      (canChangeAsset && !workingAssetId) ||
      externalReferenceTooLong
  );

  useEffect(() => {
    onSaveDisabledChange?.(isSaveDisabled);
  }, [isSaveDisabled, onSaveDisabledChange]);

  return (
    <>
      <AttributesForm
        loading={loading || selectableLoading}
        saving={saving}
        selections={jsonSelections}
        columns={columns}
        onSave={onSave ? handleSave : null}
        onCancel={handleCancel}
        onChange={handleOnChange}
        saveDisabled={isSaveDisabled}
        showComment={showComment}
        formLayout={formLayout}
        saveButtonText={isEmpty(damage) ? t('common.create', 'Create') : t('common.save', 'Save')}
        canChangeAsset={canChangeAsset}
        assetId={assetId}
        selectedAsset={selectedAsset}
        assetSelectionScopeId={assetSelectionScopeId}
        setAssetId={setWorkingAssetId}
        autoSelectAsset={isNewObservation}
        canChangeSchema={canChangeSchema}
        schemaId={workingSchemaId || schemaId}
        setSchemaId={handleChangeWorkingSchemaId}
        canChangeCritical={isEditor}
        critical={workingCritical}
        setCritical={setWorkingCritical}
        canEditExternalReference={!!externalReference}
        externalId={workingExternalId}
        setExternalId={setWorkingExternalId}
        externalIdSource={workingExternalIdSource}
        setExternalIdSource={setWorkingExternalIdSource}
        includesRealtimePotentialAttrs={includesRealtimePotentialAttrs}
        potentialAttrs={potentialAttrs}
        potentialAssets={potentialAssets}
        backgroundColor={backgroundColor}
        isImageViewer={isImageViewer}
        onRequestDeltaTMeasurement={onRequestDeltaTMeasurement}
        deltaTMeasurementEnabled={deltaTMeasurementEnabled}
        attributeLayout={attributeLayout}
      />
      <Modal
        // @ts-ignore
        cancelButtonProps={{ 'data-testid': 'damage-confirm-cancelBtn' }}
        cancelText={t('common.cancel', 'Cancel')}
        confirmLoading={saving}
        destroyOnHidden
        // @ts-ignore
        okButtonProps={{ 'data-testid': 'damage-confirm-confirmBtn' }}
        okText={t('common.continue', 'Continue')}
        okType="danger"
        onCancel={() => setWarningModalOpen(false)}
        onOk={() => _handleSave(payload)}
        title={t('damage.changeSchemaTitle', 'Change Schema?')}
        open={warningModalOpen}
      >
        {warningModalCopy}
      </Modal>
    </>
  );
}
